import { useCanViewEmails } from '../../../../../helpers/useCanViewEmails';
import React, { useEffect, useState } from 'react';
import { Droppable, Draggable, DragDropContext, DropResult } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { isHotkeyPressed } from 'react-hotkeys-hook';
import DeleteIcon from '@mui/icons-material/Clear';
import { Tooltip } from '@material-ui/core';
import cx from 'classnames';

import { FOLDER_TYPE, ROLES, RolesLowerCase, USER, USER_NEW } from '../../../../../enums';
import { getAllUsers, getAllUsersByOrgId, getOrganizationUsers } from '../../../../../api';
import { ReactComponent as FolderIcon } from '../../../../../assets/icons/folder-purple-new.svg';
import { ReactComponent as ComposerIcon } from '../../../../../assets/icons/composer.svg';
import { SortFields, SortType } from '../../../../../enums/SORT';
import { useAppSelector } from '../../../../../store/hooks';
import { ContainedButton, OutlinedButton } from '../../../Buttons';
import { bytesToGB } from '../../../../../helpers';
import { User } from '../../../OrganizationCard';
import SearchInput from '../../../SearchInput';
import RolePlate from '../../../RolePlate';
import Avatar from '../../../Avatar';
import type { DeleteUserContentItem } from '../DeleteUserModal';
import styles from './FolderTransfer.module.scss';

export interface UserEXTENDED extends USER_NEW {
  folders?: DeleteUserContentItem[];
}

type TFolderTransfer = {
  user: USER;
  userFolders: DeleteUserContentItem[];
  setLoading: (b: boolean) => void;
  onSubmit: (userToDelete: USER, userListWithFolders: UserEXTENDED[]) => Promise<void>;
  loading: boolean;
  onClose: () => void;
  orgId?: string;
};

const isOnlineComposerItem = (item: DeleteUserContentItem): item is Extract<DeleteUserContentItem, { transferType: 'onlineComposer' }> =>
  item.transferType === 'onlineComposer';
const getItemKey = (item: DeleteUserContentItem) => `${isOnlineComposerItem(item) ? 'onlineComposer' : 'folder'}-${item.id}`;
const getItemName = (item: DeleteUserContentItem) => (isOnlineComposerItem(item) ? item.title : item.name);
const getItemSize = (item: DeleteUserContentItem) =>
  isOnlineComposerItem(item)
    ? (item.videos || []).reduce((acc, video) => acc + Number(video.size || 0), 0)
    : (item.videofeeds || []).reduce(
        (accFeed, curFeed) => accFeed + (curFeed.videos || []).reduce((accVid, curVid) => accVid + Number(curVid.size || 0), 0),
        0,
      );
const getItemCount = (item: DeleteUserContentItem) => (isOnlineComposerItem(item) ? item.videos?.length || 0 : item.videofeeds.length);
const getItemIcon = (item: DeleteUserContentItem) =>
  isOnlineComposerItem(item) ? (
    <ComposerIcon className={styles.itemIcon} style={{ color: '#39b3ea' }} />
  ) : (
    <FolderIcon className={styles.itemIcon} />
  );

const FolderTransfer: React.FC<TFolderTransfer> = ({ user, userFolders, setLoading, onSubmit, loading, onClose, orgId }) => {
  const canViewEmails = useCanViewEmails();
  const { role: userRole } = useAppSelector((state) => state.user);
  const [currentRole, setCurrentRole] = useState<RolesLowerCase | 'all'>('all');

  const [searchFolder, setSearchFolder] = useState('');
  const [searchUser, setSearchUser] = useState('');

  const [usersList, setUsersList] = useState<UserEXTENDED[]>([]);
  const [folderList, setFolderList] = useState<DeleteUserContentItem[]>([]);

  useEffect(() => {
    setFolderList(userFolders);
  }, [userFolders]);

  const fetchUsers = async () => {
    const rolesQuery = [RolesLowerCase.ORGANIZATION_ADMIN, RolesLowerCase.ANALYST];
    setLoading(true);
    try {
      const res = await getOrganizationUsers({
        role: rolesQuery,
        page: 0,
        limit: 2000,
        search: '',
        sort: SortType.ASC,
        sortField: SortFields.NAME,
        orgId,
      });
      const users = res.data.documents.filter((u: USER_NEW) => u.user.id !== user.id && !u.user.invitation_token);
      setUsersList(users);
    } catch (err) {
      console.warn(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const onUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchUser(e.target.value);
  };

  const onFolderSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFolder(e.target.value);
  };

  const handleDragEnd = (result: DropResult) => {
    setDragging(false);
    setDraggingItemId(null);

    try {
      const userIndex = usersList.findIndex((u) => u.id === result.destination?.droppableId);
      const uniqueFolders = [folderList.find((f) => getItemKey(f) === result.draggableId), ...selectedFolders].filter(
        (obj, index, self): obj is DeleteUserContentItem =>
          !!obj && index === self.findIndex((t) => !!t && getItemKey(t) === getItemKey(obj)),
      );

      if (uniqueFolders.length && userIndex !== -1) {
        const newFoldersList = folderList.filter((folder) => !uniqueFolders.find((f) => getItemKey(folder) === getItemKey(f)));
        const newUserList = [...usersList];

        if (newUserList[userIndex].folders) {
          // @ts-ignore
          newUserList[userIndex].folders = [...newUserList[userIndex].folders!, ...uniqueFolders];
        } else {
          // @ts-ignore
          newUserList[userIndex].folders = uniqueFolders;
        }
        setUsersList(newUserList);
        setFolderList(newFoldersList);
        setSelectedFolders([]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const [dragging, setDragging] = useState(false);
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [selectedFolders, setSelectedFolders] = useState<DeleteUserContentItem[]>([]);

  const onFolderSelect = (folderId: string) => {
    setDragging(false);
    if (isHotkeyPressed('Ctrl') || isHotkeyPressed('Meta')) {
      if (selectedFolders.find((f) => getItemKey(f) === folderId)) {
        const newFolders = selectedFolders.filter((f) => getItemKey(f) !== folderId);
        setSelectedFolders(newFolders);
        return;
      }
      const folder = folderList.find((f) => getItemKey(f) === folderId);
      if (folder) {
        setSelectedFolders([...selectedFolders, folder]);
      }
    } else {
      if (selectedFolders.find((f) => getItemKey(f) === folderId)) {
        setSelectedFolders([]);
        return;
      }
      const folder = folderList.find((f) => getItemKey(f) === folderId);
      if (folder) {
        setSelectedFolders([folder]);
      }
    }
  };

  const onFolderRemove = (folderId: string, userIndex: number) => {
    setDragging(false);
    const folder = usersList[userIndex].folders?.find((f) => getItemKey(f) === folderId);
    if (folder) {
      const newUserList = [...usersList];
      newUserList[userIndex].folders = newUserList[userIndex].folders?.filter((f) => getItemKey(f) !== getItemKey(folder));
      setUsersList(newUserList);
      setFolderList([...folderList, folder]);
    }
  };

  const onDragStart = (start: any) => {
    setDragging(true);
    setDraggingItemId(start.draggableId);

    const draggedFolder = folderList.find((f) => getItemKey(f) === start.draggableId);

    if (draggedFolder && !selectedFolders.find((f) => getItemKey(f) === start.draggableId)) {
      setSelectedFolders([draggedFolder]);
    }
  };

  const [step, setStep] = useState(1);
  const toConfirmStep = () => {
    setStep(2);
  };
  const submit = () => {
    onSubmit(user, usersList);
  };

  return (
    <>
      {step === 1 && (
        <div className={styles.topText}>
          Click and drag the content from the left column to on top of the name of the User you wish to allocate the content to in the right
          column.
        </div>
      )}
      <div className={styles.wrapper}>
        {step === 1 && (
          <DragDropContext onDragEnd={handleDragEnd} onDragStart={onDragStart}>
            <div className={styles.leftSide}>
              <div className={styles.heading}>List of {user?.username ? user.username : (canViewEmails ? user.email : 'User')} content</div>

              <div className={styles.toolbar}>
                <SearchInput className={styles.search} onChange={onFolderSearch} />
              </div>
              <Droppable droppableId={'listOfFolders'}>
                {(provided, snapshot) => (
                  <div className={styles.listOfFolders} {...provided.droppableProps} ref={provided.innerRef}>
                    {folderList
                      .filter((f) => getItemName(f).toLowerCase().includes(searchFolder.toLowerCase()))
                      .map((f, index) => {
                        const itemKey = getItemKey(f);
                        const isSelected = selectedFolders.find((selFolder) => getItemKey(selFolder) === itemKey);
                        const shouldHideDraggingFolder = dragging && !!isSelected && draggingItemId !== itemKey;

                        return (
                          <Draggable key={itemKey} draggableId={itemKey} index={index}>
                            {(provided, snapshot) => (
                              <div
                                onClick={() => onFolderSelect(itemKey)}
                                className={cx(
                                  styles.folder,
                                  isSelected ? styles.selectedFolder : '',
                                  shouldHideDraggingFolder ? styles.hideDraggingFolder : '',
                                )}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <div className={styles.folderName}>
                                  <DragIndicatorIcon className={styles.dragIcon} />
                                  {getItemIcon(f)} {getItemName(f)}
                                </div>
                                <div className={styles.folderData}>
                                  <div className={styles.fileCount}>{getItemCount(f)} Files</div>
                                  <div className={styles.divider} />
                                  <div className={styles.fileSize}>{bytesToGB(getItemSize(f), 2)} GB</div>
                                </div>

                                {snapshot.isDragging && selectedFolders.length > 0 && (
                                  <div className={styles.draggingList}>
                                    {selectedFolders.map((f, i) => (
                                      <div key={`draggin_item_${getItemKey(f)}`} className={styles.draggingItem}>
                                        <div className={styles.draggingName}>
                                          {getItemIcon(f)} <span>{getItemName(f)} </span>
                                        </div>
                                        <div className={styles.draggingFolderData}>
                                          <div className={styles.fileCount}>{getItemCount(f)} Files</div>
                                          <div className={styles.divider} />
                                          <div className={styles.fileSize}>{bytesToGB(getItemSize(f), 2)} GB</div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
            <div className={styles.rightSide}>
              <div className={styles.heading}>Assign to</div>
              <div className={styles.toolbar}>
                <SearchInput className={styles.search} onChange={onUserSearch} />{' '}
                <div className={styles.rolesButtons}>
                  <div onClick={() => setCurrentRole('all')} className={cx(styles.role, currentRole === 'all' && styles.roleButtonActive)}>
                    All
                  </div>
                  <div
                    onClick={() => setCurrentRole(RolesLowerCase.ORGANIZATION_ADMIN)}
                    className={cx(styles.role, currentRole === RolesLowerCase.ORGANIZATION_ADMIN && styles.roleButtonActive)}
                  >
                    OA
                  </div>
                  <div
                    onClick={() => setCurrentRole(RolesLowerCase.ANALYST)}
                    className={cx(styles.role, currentRole === RolesLowerCase.ANALYST && styles.roleButtonActive)}
                  >
                    Analyst
                  </div>
                </div>
              </div>
              <div className={styles.userList}>
                {usersList
                  .filter(
                    (u) =>
                      u?.user.username?.toLowerCase()?.includes(searchUser?.toLowerCase()) ||
                      u?.user.email?.toLowerCase()?.includes(searchUser?.toLowerCase()),
                  )
                  .filter((u) => (currentRole === 'all' ? true : u.role.title === currentRole))
                  .map((u, userIndex) => (
                    <Droppable key={u.id} droppableId={u.id}>
                      {(provided, snapshot) => (
                        <div
                          className={cx(styles.user, snapshot.isDraggingOver ? styles.purpleBorder : '')}
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          <div className={styles.userLeftSide}>
                            {' '}
                            <Avatar avatar={u?.user.avatar_url} name={u.user.email} />
                            <div className={styles.userInfo}>
                              <div className={styles.userName}> {u?.user.username} </div>
                              {canViewEmails && (<div className={styles.userEmail}> {u.user.email} </div>)}
                            </div>{' '}
                          </div>
                          <div className={styles.dropZone} style={{ width: '145px' }}>
                            {provided.placeholder}

                            <div className={styles.droppedFolderList}>
                              {u?.folders &&
                                u.folders.map((f) => (
                                  <div key={`${getItemKey(f)}_dropped_folder`} className={styles.dropFolder}>
                                    <DeleteIcon
                                      onClick={() => onFolderRemove(getItemKey(f), userIndex)}
                                      className={styles.removeIcon}
                                      fontSize="small"
                                    />
                                    {getItemIcon(f)}
                                    {getItemName(f)}
                                  </div>
                                ))}
                            </div>
                          </div>
                          <Tooltip title={u.user.invitation_token ? 'Not Activated' : 'Active'}>
                            <div className={styles.rolePlate}>
                              <div className={cx(styles.rolePlateInner, u.user.invitation_token && styles.roleNotActive)}>
                                <RolePlate userRole={u.role.title} />
                              </div>
                            </div>
                          </Tooltip>
                        </div>
                      )}
                    </Droppable>
                  ))}
              </div>
            </div>
            <div className={styles.shadowHelper} />
          </DragDropContext>
        )}

        {step === 2 && (
          <div className={styles.deleteConfirmModal}>
            <div className={styles.confirmTitle}>Are you sure that you want to delete {user?.username ? user.username : (canViewEmails ? user.email : 'User')}?</div>
            <div className={styles.list}>
              {' '}
              {usersList
                .filter((u) => u?.folders?.length)
                .map((u) => (
                  <div key={`user_transfer_${u.id}`}>
                    {' '}
                    This content will be transferred to {u?.user.username ? u.user.username : (canViewEmails ? u.user.email : 'User')}:
                    <ul>
                      {u.folders?.map((f) => (
                        <li key={getItemKey(f)}>"{getItemName(f)}"</li>
                      ))}
                    </ul>
                  </div>
                ))}{' '}
              {folderList.length > 0 && (
                <div>
                  This content will be deleted:
                  <ul>
                    {folderList?.map((f) => (
                      <li key={getItemKey(f)}>"{getItemName(f)}"</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={cx(styles.actions, step === 2 ? styles.flexColumn : '')}>
        <ContainedButton
          onClick={step === 1 ? toConfirmStep : submit}
          disabled={userFolders.length === folderList.length || loading}
          className={cx(styles.actionButton, step === 2 ? styles.btnShortWidth : '')}
          kind="purple"
        >
          {step === 1 && 'Share'}
          {step === 2 && 'Delete User'}
        </ContainedButton>
        {step === 2 && (
          <OutlinedButton
            onClick={onClose}
            disabled={loading}
            className={cx(styles.actionButton, step === 2 ? styles.btnShortWidth : '')}
            kind="default"
          >
            Cancel
          </OutlinedButton>
        )}
      </div>
    </>
  );
};

export default FolderTransfer;
